/**
 * WorkLifeFilterContext
 *
 * Global Work/Life context toggle.
 * - 'all'  → show everything (default)
 * - 'work' → show only work-mode areas, projects, tasks
 * - 'life' → show only life-mode areas, projects, tasks
 *
 * Usage:
 *   const { filter, setFilter } = useWorkLifeFilter();
 *   const filteredAreas = filterByMode(areas, filter);
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { storage } from '../utils/storage';
import useAreaStore from '../stores/useAreaStore';
import useProjectStore from '../stores/useProjectStore';

const STORAGE_KEY = 'work_life_filter';

const WorkLifeFilterContext = createContext({
    filter: 'all',
    setFilter: () => {},
    filteredAreas: [],
    filteredProjects: [],
    filterByMode: (items) => items,
});

export function WorkLifeFilterProvider({ children }) {
    const [filter, setFilterState] = useState(() =>
        storage.get(STORAGE_KEY, 'all')
    );

    const areas = useAreaStore(s => s.areas);
    const projects = useProjectStore(s => s.projects);

    const setFilter = useCallback((newFilter) => {
        setFilterState(newFilter);
        storage.set(STORAGE_KEY, newFilter);
    }, []);

    /**
     * Returns true if an area/item should be visible given the current filter.
     * mode: 'work' | 'life' | 'both' | undefined
     * If mode is missing, item is always visible.
     */
    const matchesFilter = useCallback((mode) => {
        if (filter === 'all') return true;
        if (!mode || mode === 'both') return true;
        return mode === filter;
    }, [filter]);

    const filteredAreas = useMemo(() =>
        areas.filter(a => matchesFilter(a.mode)),
        [areas, matchesFilter]
    );

    const filteredProjects = useMemo(() => {
        if (filter === 'all') return projects;
        // A project matches if its parent area matches (or it has no area)
        const matchingAreaIds = new Set(filteredAreas.map(a => a.id));
        return projects.filter(p => !p.areaId || matchingAreaIds.has(p.areaId));
    }, [projects, filteredAreas, filter]);

    const filterByMode = useCallback((items, getMode) => {
        if (filter === 'all') return items;
        return items.filter(item => {
            const mode = getMode ? getMode(item) : item.mode;
            return !mode || mode === 'both' || mode === filter;
        });
    }, [filter]);

    const value = useMemo(() => ({
        filter,
        setFilter,
        filteredAreas,
        filteredProjects,
        filterByMode,
    }), [filter, setFilter, filteredAreas, filteredProjects, filterByMode]);

    return (
        <WorkLifeFilterContext.Provider value={value}>
            {children}
        </WorkLifeFilterContext.Provider>
    );
}

export function useWorkLifeFilter() {
    return useContext(WorkLifeFilterContext);
}
